import {
  PigRunVerifiedPassageData,
  VerifiedPassageEntry,
} from '../models/PigRunVerifiedPassageData';
import PigRunService from '../services/PigRunService';

export interface PassageOrderingValidationResult {
  valid: boolean;
  message?: string;
}

export interface PassageVerificationValidationResult {
  valid: boolean;
  warning?: string;
  error?: string;
}

/**
 * Performs all passage verification checks:
 * 1. Ensures at most one verified passage exists at the given marker.
 * 2. Validates passage timestamp ordering against verified passages at other markers.
 *
 * Returns { valid: true } if verification may proceed.
 * Returns { valid: false, warning } for a business-rule violation (display as warning).
 * Returns { valid: false, error } for an API/network failure (display as error).
 */
export async function validatePassageVerification(
  pipelineSectionMarkerId: number,
  pigRunId: number | null | undefined,
  passageTimestamp: Date | string
): Promise<PassageVerificationValidationResult> {
  // Check if this marker already has a verified passage
  try {
    const response = await PigRunService.hasVerifiedPassageAtMarker(
      pipelineSectionMarkerId
    );
    if (response.data === true) {
      return {
        valid: false,
        warning:
          'This marker already has a verified passage. Only one verified passage is permitted per marker.',
      };
    }
  } catch (error) {
    console.error('Failed to check verified passage status:', error);
    return {
      valid: false,
      error: 'Unable to verify passage. Please try again.',
    };
  }

  // Validate passage timestamp ordering against verified passages at other markers
  if (pigRunId) {
    try {
      const vpResponse = await PigRunService.getVerifiedPassageData(pigRunId);
      const result = validatePassageTimestampOrdering(
        vpResponse.data,
        pipelineSectionMarkerId,
        passageTimestamp
      );
      if (!result.valid) {
        return { valid: false, warning: result.message };
      }
    } catch (error) {
      console.error('Failed to validate passage timestamp ordering:', error);
      return {
        valid: false,
        error: 'Unable to verify passage. Please try again.',
      };
    }
  }

  return { valid: true };
}

/**
 * Validates that a passage timestamp at a given marker does not violate
 * chronological ordering relative to existing verified passages along the pipeline.
 *
 * Rules:
 * - A passage cannot be verified if its timestamp >= a verified passage timestamp
 *   at a downstream marker (higher sort order).
 * - A passage cannot be verified if its timestamp <= a verified passage timestamp
 *   at an upstream marker (lower sort order).
 */
export function validatePassageTimestampOrdering(
  data: PigRunVerifiedPassageData,
  currentMarkerId: number,
  passageTimestamp: Date | string
): PassageOrderingValidationResult {
  const currentMarker = data.markers.find(
    (m) => m.pipelineSectionMarkerId === currentMarkerId
  );
  if (!currentMarker) {
    return { valid: true };
  }

  const currentSortOrder = currentMarker.sortOrder;
  const ts =
    typeof passageTimestamp === 'string'
      ? toUtcTime(passageTimestamp)
      : passageTimestamp.getTime();

  // Check downstream violation: verified passage at higher sort order with timestamp <= passageTimestamp
  const downstreamViolation = data.verifiedPassages.find(
    (vp: VerifiedPassageEntry) =>
      vp.sortOrder > currentSortOrder && toUtcTime(vp.passageTimeStamp) <= ts
  );

  if (downstreamViolation) {
    const markerName = getMarkerName(
      data,
      downstreamViolation.pipelineSectionMarkerId
    );
    return {
      valid: false,
      message: `Cannot verify passage. The passage timestamp is greater than or equal to a verified passage at downstream marker '${markerName}'.`,
    };
  }

  // Check upstream violation: verified passage at lower sort order with timestamp >= passageTimestamp
  const upstreamViolation = data.verifiedPassages.find(
    (vp: VerifiedPassageEntry) =>
      vp.sortOrder < currentSortOrder && toUtcTime(vp.passageTimeStamp) >= ts
  );

  if (upstreamViolation) {
    const markerName = getMarkerName(
      data,
      upstreamViolation.pipelineSectionMarkerId
    );
    return {
      valid: false,
      message: `Cannot verify passage. The passage timestamp is less than or equal to a verified passage at upstream marker '${markerName}'.`,
    };
  }

  return { valid: true };
}

function toUtcTime(timestamp: string): number {
  if (/Z|[+-]\d{2}:\d{2}$/.test(timestamp)) {
    return new Date(timestamp).getTime();
  }
  return new Date(timestamp + 'Z').getTime();
}

function getMarkerName(
  data: PigRunVerifiedPassageData,
  pipelineSectionMarkerId: number
): string {
  const marker = data.markers.find(
    (m) => m.pipelineSectionMarkerId === pipelineSectionMarkerId
  );
  return marker?.name ?? 'Unknown';
}
